import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useFieldArray, useForm } from "react-hook-form"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  PREFIX,
  SELECTED_SOLD_BY_USER,
} from "src/main/PointOfSale/storage_keys"
import { v4 as uuidv4 } from "uuid"

import {
  addCartItem,
  createCart as createCartApi,
  getCart,
  removeCartItem,
  updateCart as updateCartApi,
  updateCartItem,
} from "src/api/PointOfSale/cart"

import useDebounce from "src/hooks/use_debounce"
import { useLocalStorageState } from "src/hooks/use_local_storage_state"
import { useToast } from "src/hooks/use_toast"
import useWindowSize from "src/hooks/use_window_size"

import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

const PanelState = Object.freeze({
  CLOSED: "closed",
  OPEN: "open",
})

const useServerCart = (marinaUsers, useNewTaxExperience = false) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const cartIdQueryParameter = searchParams.get("cart_id")
  const marinaSlug = getCurrentMarinaSlug()
  const queryClient = useQueryClient()
  const showToast = useToast()
  const { isLargeScreen } = useWindowSize()
  const [panelState, setPanelState] = useState({})
  const [cartId, setCartId] = useState(cartIdQueryParameter)
  const addItemMutex = useRef(Promise.resolve())
  const cartInitialized = useRef(false)

  const {
    data: cartData,
    isLoading: cartDataLoading,
    isFetching: cartDataFetching,
    isFetched: cartDataFetched,
    failureCount: cartFetchFailures,
  } = useQuery({
    queryKey: ["cart", marinaSlug, cartId],
    queryFn: async () => {
      const response = await getCart({ marinaSlug, cartId })
      return response.data
    },
    initialData: {
      id: cartId,
      note: "",
      contactId: null,
      contactBoat: null,
      soldById: null,
      cartItems: [],
      lineItems: [],
      totals: {
        subtotal: null,
        discount: null,
        tax: null,
        total: null,
      },
    },
    enabled: !!cartId,
  })

  const methods = useForm({
    defaultValues: { cart: [], note: "" },
    mode: "all",
    values: { cart: cartData.cartItems, note: cartData.note },
  })

  const { fields, remove } = useFieldArray({
    name: "cart",
    control: methods.control,
    keyName: "formId",
  })

  /* Sync cart id to query params */

  useEffect(() => {
    if (cartId && searchParams.get("cart_id") !== cartId) {
      setSearchParams({ cart_id: cartId }, { replace: true })
    }
  }, [cartId, searchParams, setSearchParams])

  useEffect(() => {
    if (!cartData || cartDataFetching) return

    if (cartData.dockwaPlusDiscountAdded) {
      showToast("Dockwa+ member discount added", { type: "success" })
    }
  }, [cartData, cartDataFetching, showToast])

  /* Contact and Boat Management */

  const [contact, setContactState] = useState(null)
  const [boat, setBoatState] = useState(null)
  const [dockwaPayPaymentMethod, setDockwaPayPaymentMethod] = useState(null)

  // Every cart mutation writes its response into the query cache, so a response
  // that left the server before a customer write lands here after it, carrying
  // the pre-write pair. Hydrating from that would drop a just-picked boat. While
  // a write is outstanding, only a response that already reflects it is allowed
  // to hydrate.
  const pendingCustomerWrite = useRef(null)

  useEffect(() => {
    if (!cartData || cartDataFetching) return

    const { contact: cartContact, contactBoat: cartBoat } = cartData

    if (pendingCustomerWrite.current) {
      const { contactId, contactBoatId } = pendingCustomerWrite.current
      const settled =
        (cartContact?.id || null) === contactId &&
        (cartBoat?.id || null) === contactBoatId
      if (!settled) return

      pendingCustomerWrite.current = null
    }

    // The server has no concept of Guest — when the user has explicitly
    // picked Guest locally, preserve that selection across cart refreshes
    // rather than letting cartContact:null silently clear it.
    setContactState((prev) => {
      if (prev?.isGuest && !cartContact) return prev
      return cartContact || null
    })

    // No Guest-style guard: unlike the contact, the boat has no client-only
    // value to preserve, so the cart is always the authority.
    setBoatState(cartBoat || null)
  }, [cartData, cartDataFetching])

  /* Sold By User */

  const [defaultSoldByUser, setDefaultSoldByUser] = useLocalStorageState(
    PREFIX,
    SELECTED_SOLD_BY_USER
  )

  const [soldByUser, setSoldByUserState] = useState(null)

  useEffect(() => {
    if (!defaultSoldByUser) {
      setDefaultSoldByUser(marinaUsers[0])
    }
  }, [defaultSoldByUser, marinaUsers, setDefaultSoldByUser])

  useEffect(() => {
    const { id, soldById } = cartData

    if (!id) return
    if (soldByUser) return
    if (!soldById) return

    setSoldByUserState(marinaUsers.find((user) => user.id === soldById))
  }, [cartData, cartDataFetching, marinaUsers, soldByUser])

  const { mutate: syncSoldBy } = useMutation({
    mutationFn: async (soldById) => {
      const { data: updatedCart } = await updateCartApi({
        marinaSlug,
        cart: { soldById },
        cartId: cartData.id,
      })
      return updatedCart
    },
    onMutate: (soldById) => {
      const previousSoldById = soldByUser?.id
      setSoldByUserState(marinaUsers.find((user) => user.id === soldById))

      return { previousSoldById }
    },
    onError: (error, variables, context) => {
      console.error(error)
      setSoldByUserState(
        marinaUsers.find((user) => user.id === context.previousSoldById)
      )
      showToast(`Failed to update sold by user: ${error.message}`, {
        type: "error",
      })
    },
    onSuccess: (cart) => {
      queryClient.setQueryData(["cart", marinaSlug, cartId], cart)
    },
  })

  const setSoldByUser = useCallback(
    (user) => {
      syncSoldBy(user.id)
      setDefaultSoldByUser(user)
    },
    [setDefaultSoldByUser, syncSoldBy]
  )

  /* Initialize a cart if one is not provided */

  const { mutate: createCart, isLoading: isCreatingCart } = useMutation({
    mutationFn: async ({ soldById }) => {
      const { data: cart } = await createCartApi({
        marinaSlug,
        soldById,
      })
      return cart
    },
    onMutate: async ({ cartId: previousCartId }) => {
      const previousCart = queryClient.getQueryData([
        "cart",
        marinaSlug,
        previousCartId,
      ])
      queryClient.removeQueries(["cart", marinaSlug])
      return { previousCart }
    },
    onSuccess: async (cart) => {
      setCartId(cart.id)
      setSoldByUserState(marinaUsers.find((user) => user.id === cart.soldById))
      await queryClient.setQueryData(["cart", marinaSlug, cart.id], cart)
      setPanelState({})
    },
    onError: (error, variables, context) => {
      console.error(error)
      queryClient.setQueryData(
        ["cart", marinaSlug, context.previousCart.id],
        context.previousCart
      )
      showToast("Failed to clear cart", { type: "error" })
    },
  })

  useEffect(() => {
    if (!cartData || cartDataFetching) return
    if (cartData.status === "expired") {
      navigate("/", { replace: true })
    }
  }, [cartData, cartDataFetching, navigate])

  useEffect(() => {
    if (!cartId && !cartInitialized.current && defaultSoldByUser) {
      cartInitialized.current = true
      createCart({ cartId, soldById: defaultSoldByUser.id })
    }
  }, [cartId, createCart, defaultSoldByUser])

  /* Update Sale Note */

  const { mutate: updateNote } = useMutation({
    mutationFn: async (updates) => {
      const { data: updatedCart } = await updateCartApi({
        marinaSlug,
        cart: updates,
        cartId: cartData.id,
      })
      return updatedCart
    },
    onSuccess: (cart) => {
      queryClient.setQueryData(["cart", marinaSlug, cartId], cart)
    },
    onError: (error) => {
      showToast(`Failed to update the sale: ${error.message}`, {
        type: "error",
      })
    },
  })

  const [debouncedUpdateNote] = useDebounce(updateNote, 1000)

  useEffect(() => {
    if (cartId === null) return

    const subscription = methods.watch((formData, { name }) => {
      if (name === "note") {
        debouncedUpdateNote({ note: formData.note })
      }
    })

    return () => subscription.unsubscribe()
  }, [cartId, debouncedUpdateNote, methods])

  /* Update Item */

  const { mutate: updateItem, isLoading: isUpdatingItem } = useMutation({
    mutationFn: async ({ item }) => {
      const { data } = await updateCartItem({
        marinaSlug,
        cartId: cartData.id,
        itemId: item.id,
        item,
      })
      return data
    },
    onSuccess: (cart) => {
      queryClient.setQueryData(["cart", marinaSlug, cartId], cart)
    },
    onError: (error) => {
      showToast(`There was an error updating the item: ${error.message}`, {
        type: "error",
      })
    },
  })

  /* Add Item */

  const { mutateAsync: addItem, isLoading: isAddingItem } = useMutation({
    mutationFn: async ({ item }) => {
      const { data: cart } = await addCartItem({
        marinaSlug,
        cartId,
        item,
      })
      return { cart, item }
    },
    onSuccess: ({ cart, item }) => {
      queryClient.setQueryData(["cart", marinaSlug, cartId], cart)
      if (!isLargeScreen)
        showToast(`${item.name} Added`, { type: "success", duration: 2 })
    },
  })

  const addItemToCart = ({ item: product }) => {
    const item = {
      id: uuidv4(),
      name: product.name,
      productId: product.id,
      price: product.price_per_unit || 0,
      taxRate: useNewTaxExperience ? null : product.tax_rate || 0,
      quantity: product.quantity || 1,
      discount: 0,
      discountAmount: 0,
      pricePrecision: product.price_precision,
    }

    closeAllPanels()
    togglePanelOpen({ id: item.id })

    addItemMutex.current = addItemMutex.current
      .then(async () => {
        return addItem({ item })
      })
      .catch((error) => {
        showToast(`There was an error adding the item: ${error.message}`, {
          type: "error",
        })
      })

    return addItemMutex.current
  }

  /* Remove Item */

  const { mutate: removeItem, isLoading: isRemovingItem } = useMutation({
    mutationFn: async ({ itemId }) => {
      const { data: cart } = await removeCartItem({
        marinaSlug,
        cartId,
        itemId,
      })
      return { cart, itemId }
    },
    onMutate: async ({ itemId }) => {
      await queryClient.cancelQueries(["cart", marinaSlug, cartId])

      const previousCart = queryClient.getQueryData([
        "cart",
        marinaSlug,
        cartId,
      ])

      // Store the item's index in the form array for potential rollback
      const formIndex = fields.findIndex((field) => field.id === itemId)
      const removedItem = formIndex !== -1 ? fields[formIndex] : null

      queryClient.setQueryData(["cart", marinaSlug, cartId], (old) => ({
        ...old,
        cartItems: old.cartItems.filter((item) => item.id !== itemId),
      }))

      return { previousCart, formIndex, removedItem }
    },
    onSuccess: async ({ cart }) => {
      queryClient.setQueryData(["cart", marinaSlug, cartId], cart)
    },
    onError: (err, variables, context) => {
      console.error(err)

      // Restore the server state
      queryClient.setQueryData(
        ["cart", marinaSlug, cartId],
        context.previousCart
      )

      // Restore the form state if we removed an item and have the context
      if (context.formIndex !== -1 && context.removedItem) {
        const currentCartValues = methods.getValues("cart") || []
        const updatedCart = [
          ...currentCartValues.slice(0, context.formIndex),
          context.removedItem,
          ...currentCartValues.slice(context.formIndex),
        ]

        // Reset the cart field with our modified array that includes the previously removed item
        methods.setValue("cart", updatedCart)
      }

      showToast(`Failed to remove item from cart: ${err.message}`, {
        type: "error",
      })
    },
  })

  const removeItemFromCart = (itemId) => {
    // Find the index of the item in the form fields array
    const formIndex = fields.findIndex((field) => field.id === itemId)

    // If found, remove it from the form state
    if (formIndex !== -1) {
      remove(formIndex)
    }

    // Continue with server-side removal
    removeItem({ itemId })
  }

  /* Update Cart */

  const { mutate: updateCart, isLoading: isUpdatingCart } = useMutation({
    mutationFn: async (updates) => {
      const { data: updatedCart } = await updateCartApi({
        marinaSlug,
        cart: updates,
        cartId: cartData.id,
      })
      return updatedCart
    },
    onSuccess: (cart) => {
      queryClient.setQueryData(["cart", marinaSlug, cartId], cart)
    },
    onError: (error) => {
      // The write never landed, so nothing is waiting to be reconciled — let
      // the server resume being the authority rather than block hydration.
      pendingCustomerWrite.current = null
      showToast(`Failed to update the sale: ${error.message}`, {
        type: "error",
      })
    },
  })

  /* Clear Cart */

  const clearCart = async () => {
    // soldByUser briefly becomes null after a sale completes; Cart#sold_by
    // validates presence on the server, so resolve from staff session,
    // saved default, or the just-completed cart before creating the next.
    const soldById =
      soldByUser?.id ?? defaultSoldByUser?.id ?? cartData?.soldById
    if (!soldById) {
      showToast("Pick a Sold By to start a new sale", { type: "warning" })
      return
    }

    setContactState(null)
    setBoatState(null)
    setDockwaPayPaymentMethod(null)

    await createCart({ cartId, soldById })
  }

  const refreshCart = async () => {
    await queryClient.invalidateQueries(["cart", marinaSlug, cartId])
  }

  /* Contact and Boat Management */

  const isCartCompleted = cartData.status === "completed"

  const setContact = useCallback(
    (newContact) => {
      const newIsGuest = !!newContact?.isGuest
      const hasIdentity = !!(newContact && (newContact.id || newIsGuest))
      const hadIdentity = !!contact

      if (!hasIdentity && !hadIdentity) return

      const newKey = hasIdentity ? newContact.id ?? "guest" : null
      const prevKey = hadIdentity
        ? contact.id ?? (contact.isGuest ? "guest" : null)
        : null

      const customerChanged = prevKey !== newKey

      if (hasIdentity) {
        if (customerChanged) {
          showToast("Customer added", { type: "success" })
        }
        setContactState(newContact)
      } else {
        showToast("Customer removed", { type: "success" })
        setContactState(null)
      }

      // Only an actual change of customer touches the boat. Several call sites
      // re-send the same customer to refresh something else on it — a new saved
      // card, a Dockwa Pay confirmation — and those must not drop the boat.
      // When the customer does change the boat belongs to the one being
      // replaced, so the clear goes in the same write: a separate PATCH would
      // race this one.
      if (customerChanged) {
        if (cartId && !isCartCompleted) {
          const updates = {
            contactId: newContact?.id || null,
            contactBoatId: null,
          }
          pendingCustomerWrite.current = updates
          updateCart(updates)
        }

        setBoatState(null)
      }

      setDockwaPayPaymentMethod(null)
    },
    [contact, cartId, isCartCompleted, showToast, updateCart]
  )

  // The picker resolves the customer and the boat together, and both live on the
  // cart, and this is the only way either reaches it. Setting them separately
  // would put two PATCHes in flight; if the contact one lands second it clears
  // the boat the user just picked. One write.
  //
  // `contact` is omitted, not nulled, when the selection did not name one — the
  // picker only leaves it out when nothing about the customer changed.
  const setCustomerSelection = useCallback(
    (selection) => {
      const nextBoat = selection.boat?.id ? selection.boat : null
      const hasContact = Object.hasOwn(selection, "contact")
      const nextContact = hasContact ? selection.contact : contact

      if (hasContact) {
        const prevKey = contact?.id ?? (contact?.isGuest ? "guest" : null)
        const nextKey =
          nextContact?.id ?? (nextContact?.isGuest ? "guest" : null)
        if (nextKey && prevKey !== nextKey) {
          showToast("Customer added", { type: "success" })
        }
        setContactState(nextContact || null)
        setDockwaPayPaymentMethod(null)
      }

      setBoatState(nextBoat)

      if (cartId && !isCartCompleted) {
        const updates = {
          contactId: nextContact?.id || null,
          contactBoatId: nextBoat?.id || null,
        }
        pendingCustomerWrite.current = updates
        updateCart(updates)
      }
    },
    [contact, cartId, isCartCompleted, showToast, updateCart]
  )

  /* Panel Management */

  const panelIsOpen = ({ id }) => {
    return panelState[id] === PanelState.OPEN
  }
  const closeAllPanels = () => {
    setPanelState((current) =>
      Object.fromEntries(
        Object.keys(current).map((key) => [key, PanelState.CLOSED])
      )
    )
  }
  const togglePanelOpen = ({ id }) => {
    setPanelState((current) => ({
      ...current,
      [id]:
        panelState[id] === PanelState.OPEN
          ? PanelState.CLOSED
          : PanelState.OPEN,
    }))
  }

  const totals = useMemo(() => {
    const { totals } = cartData

    return Object.fromEntries(
      Object.entries(totals).map(([key, value]) => [
        key,
        value === null ? "-.--" : (value / 100).toFixed(2),
      ])
    )
  }, [cartData])

  const priceLoading =
    cartDataLoading ||
    isCreatingCart ||
    isUpdatingCart ||
    isAddingItem ||
    isRemovingItem ||
    isUpdatingItem

  return {
    cartId,
    isLoadingCart: !!cartId && !cartDataFetched && cartFetchFailures === 0,
    cartStatus: cartData.status,
    methods,
    fields,
    addItemToCart,
    removeItemFromCart,
    clearCart,
    updateCart,
    refreshCart,
    updateItem,
    panelIsOpen,
    togglePanelOpen,
    closeAllPanels,
    totals,
    serverContact: cartData.contact,
    lineItems: cartData.lineItems,
    expectedSurcharge: cartData.expectedSurcharge,
    loading: priceLoading,
    soldByUser,
    setSoldByUser,
    contact,
    setContact,
    boat,
    setCustomerSelection,
    dockwaPayPaymentMethod,
    setDockwaPayPaymentMethod,
  }
}

export default useServerCart
